import { useEffect } from "react";
import { useScorePriorities } from "@/utils/react-query-hooks/aiAnnotation";

/**
 * Priorities on server-side aggregates, as one hook: one request, scored on
 * the server (POST scorePriorities — see okf-be _scorePriorities). Every
 * Priorities surface (the field, the table, the category cards) calls this
 * with the same scope and react-query answers once.
 *
 * The request runs over the scope WITHOUT the period (momentum reads the
 * whole series and slices it itself around the selected period).
 *
 * Returns { counts, historyTree, scored: { priorities, wins, meta } | null,
 *           seriesByNode: { [subtopicId | __unplaced:<topicId> | __unplaced]: { [periodKey]: n } },
 *           grain, loading }
 *
 * `loading` covers the request, i.e. everything every caller needs. The
 * request is shared with the Priorities page and comes from cache, so a
 * second surface paints at once.
 */

// The scorer found no earlier period with observations: one month of data
// and nothing before it. Nothing time-wise can be read then — no trend, no
// momentum, so no zones — and every surface that says so (the blurred
// widgets, the fires stat) reads this one flag.
export const isSinglePeriod = (meta) => meta?.historyPeriods === 0;

export const usePrioritiesScoring = ({
  topicClusteringLens,
  historyFilters,
  period,
  scoreGrain = "findings",
  trendWindow,
  // The comparison period by key; undefined leaves the choice to `trendWindow`.
  compareKey,
  onCountsMeta,
  // A frozen { scored, counts } answer (a public report snapshot): no
  // request is made and this is what every caller reads. Undefined live.
  prefetched,
}) => {
  const { data: fetched, isLoading } = useScorePriorities({
    topicClusteringLens,
    activeFilters: historyFilters,
    period,
    scoreGrain,
    trendWindow,
    compareKey,
    enabled: !prefetched,
  });
  const data = prefetched || fetched;
  const counts = data?.counts || null;
  useEffect(() => {
    if (counts) onCountsMeta?.(counts);
  }, [counts, onCountsMeta]);

  return {
    counts,
    historyTree: data?.historyTree || null,
    scored: data?.scored || null,
    seriesByNode: data?.seriesByNode || {},
    grain: data?.grain || "month",
    // Loading until there is a scored answer — also before a period is
    // picked, when the request has not been made yet (as the old two-request
    // hook read `!scored`).
    loading: !prefetched && (isLoading || !data?.scored),
  };
};
